<script lang="ts" setup>
import { onMounted, ref, useSlots } from '#imports';

import type { InputProps as Props } from '@/shared/interfaces';
import type { FieldVariation, InputValue } from '@/shared/types';

import useInput from '@/shared/composables/use-input';

defineOptions({ inheritAttrs: false });

const slots = useSlots();
const props = withDefaults(defineProps<Props<InputValue, FieldVariation>>(), {
	tag: 'input',
	variations: () => [],
	description: '',
	isError: false,
	errorMessage: '',
	required: false,
	type: 'text',
	hasAction: false,
	actionType: 'decorate',
});

const emit = defineEmits<{
	(e: 'update:modelValue', value: InputValue): void;
	(e: 'blur'): void;
	(e: 'reset-value'): void;
}>();

const input = ref<HTMLInputElement | HTMLTextAreaElement | null>(null);

const {
	isFocus,
	isTextarea,
	computedClass,
	computedType,
	computedListeners,
	computedActionTag,
	computedActionAttrs,
	computedControl,
	handleInput,
} = useInput(props, emit, slots);

onMounted(() => {
	setTimeout(() => {
		if (isTextarea.value && input.value !== null) {
			input.value.style.height = input.value?.scrollHeight + 'px';
		}
	}, 50);
});

const handleInputBlur = () => {
	isFocus.value = false;
	emit('blur');
};

defineExpose({ input });
</script>

<template>
	<div class="field" :class="computedClass">
		<div v-if="label || !!slots.header" class="field__header">
			<div v-if="label" class="field__header-item">
				<span class="field__label">{{ label }}</span>
			</div>

			<div v-if="!!slots.header" class="field__header-item">
				<span class="field__additional">
					<slot name="header" />
				</span>
			</div>
		</div>

		<component
			:is="computedControl"
			class="field__control"
			:style="isTextarea ? 'max-height: 100px' : 'max-height: none'"
		>
			<component
				:is="tag"
				ref="input"
				v-bind="$attrs"
				:value="modelValue"
				:type="computedType"
				:disabled="disabled"
				class="field__input"
				@input="handleInput"
				@focus="isFocus = true"
				@blur="handleInputBlur"
			/>

			<component
				:is="computedActionTag"
				v-if="!!slots.action && hasAction"
				class="field__action"
				v-bind="computedActionAttrs"
				v-on="computedListeners"
			>
				<slot name="action" />
			</component>
		</component>

		<div
			v-if="description.length > 0 && description !== errorMessage"
			class="field__description"
		>
			{{ description }}
		</div>

		<div v-if="isError" class="field__error">
			<template v-if="typeof errorMessage === 'object'">
				<div
					v-for="(message, index) in errorMessage"
					:key="index"
					class="mt-4"
				>
					{{ message }}
				</div>
			</template>
			<div v-else>
				{{ errorMessage }}
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
$focus-color: $dusty-gray;
$disabled-color: $dusty-gray;
$required-color: red;

$input-color: $black;
$input-background: $wild-sand;
$input-border-color: $wild-sand;

$input-autofill-color: $white;
$input-autofill-background: $white;

$placeholder-color: rgba($black, .5);
$placeholder-error-color: $black;

$label-color: $black;
$label-disabled-color: $black;

$is-focus-action: false;
$action-size: 16px;
$action-color: $input-color;
$action-hover-color: $input-color;

$description-color: $black;

$error-color: red;
$error-focus-color: darken(red, 25%);

@mixin focus {
	border-color: $focus-color;
	outline: none;
}

@mixin reset-focus {
	border-color: transparent;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity $transition-duration $transition-function;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.field {
	position: relative;

	&--error & {
		&__input {
			border-color: $error-color;

			&:focus {
				border-color: $error-focus-color;
			}

			&::placeholder {
				color: $placeholder-color;
			}
		}

		&__action {
			color: $error-color;

			@media (hover: hover) {
				&:hover {
					color: $error-color;
				}
			}
		}
	}

	&--error &__input:focus + &__action {
		color: $error-color;
	}

	&--required & {
		&__label {
			&::after {
				font-size: inherit;

				content: " *";

				color: $required-color;
			}
		}
	}

	&--disabled & {
		&__label {
			color: $label-disabled-color;
		}

		&__input {
			pointer-events: none;

			border-color: $disabled-color;
		}
	}

	&--has-action & {
		&__input {
			padding-right: rem(40px);
		}
	}

	&--textarea & {
		&__control {
			background-color: $input-background;
		}

		&__input {
			overflow: hidden;

			height: rem($textarea-height);
			min-height: rem($textarea-height);

			resize: none;

			background-color: initial;
		}
	}

	&--focus & {
		&__input {
			@include focus;
		}
	}

	&--textarea#{&}--focus & {
		&__control {
			@include focus;
		}

		&__input {
			@include reset-focus;
		}
	}

	&--textarea#{&}--error & {
		&__control {
			border-color: $error-color;
		}

		&__input {
			@include reset-focus();
		}
	}

	&--white & {
		&__control {
			border-color: $black;
			background-color: $white;
		}

		&__input {
			background-color: $white;
		}
	}

	&__header {
		display: flex;

		margin-bottom: rem($gap-mini);

		&-item {
			flex: 1;

			&:nth-child(2) {
				text-align: right;
			}
		}
	}

	&__label {
		@include text-medium;

		color: $label-color;
	}

	&__additional {
		@include text-medium;
	}

	&__control {
		position: relative;

		overflow: hidden;

		transition: border-color $transition-duration $transition-function;

		border: rem(1px) solid $input-border-color;
		border-radius: rem($radius-secondary);
	}

	&__input {
		@include text-small;

		width: 100%;
		height: rem($input-height);
		padding: rem(10px) rem($gap-small);

		transition:
			border-color $transition-duration $transition-function,
			background-color $transition-duration $transition-function;

		color: $input-color;
		border: rem(1px) solid $input-border-color;
		border-radius: rem($radius-secondary);
		background-color: $input-background;

		@media #{$screen-tablet} {
			padding: rem($gap-mini) rem($gap-average);
		}

		&:autofill,
		&:autofill:hover,
		&:autofill:focus {
			color: $input-autofill-color;
			background-color: $input-autofill-background;
		}

		&::placeholder {
			user-select: none;

			color: $placeholder-color;
		}
	}

	@if $is-focus-action {
		&__input:focus + &__action {
			color: $focus-color;
		}
	}

	&__action {
		$size: 40px;

		position: absolute;
		top: 0;
		right: 0;

		display: flex;
		align-items: center;
		flex-shrink: 0;
		justify-content: center;

		width: rem($size);
		height: 100%;

		user-select: none;
		transition: color $transition-function $transition-duration;

		color: $action-color;
		border: none;
		background: none;

		@media (hover: hover) {
			&:hover {
				color: $action-hover-color;
			}
		}

		:deep(.icon) {
			width: rem($action-size);
			height: rem($action-size);
			margin: auto;

			color: inherit;
		}
	}

	&__description {
		@include text-mini;

		padding-top: rem($gap-mini);

		color: $description-color;
	}

	&__error {
		@include text-mini;

		margin-top: rem($gap-mini);

		color: $error-color;
	}
}
</style>
